import {
  Button,
  CircularProgress,
  Dialog,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { format, isValid } from 'date-fns';
import { useMemo } from 'react';
import { useLastVisible } from '~/shared/hooks/useLastVisible';
import {
  defaultFeeSchedule,
  feeScheduleFromMeExpertRates,
  type FeeScheduleData,
} from '~/modules/connect/feeSchedule';
import { RetainerFeeType, type RetainerFieldsFragment } from '~/gql/graphql';
import { CENTS_PER_DOLLAR } from '~/shared/constants';
import { UserRole, type User } from '~/shared/types/connect';
import {
  getRateDisplayLabel,
  getRetainerHourlyRateDisplayContext,
  RetainerExpertCard,
  RetainerExpertCardAvatar,
  RetainerExpertCardContent,
  RetainerReceiptSummary,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { styled } from '@mui/material/styles';
import { retainerCardDescription } from '~/modules/matters/detail/retainersTabUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  retainer: RetainerFieldsFragment | null;
  getExpertById: (id: string) => User | undefined;
  isLoading?: boolean;
}

function formatAgreedAt(signedAt: string | null | undefined): string {
  if (!signedAt) {
    return '—';
  }
  const date = new Date(signedAt);
  return isValid(date) ? format(date, 'MMM d, yyyy') : '—';
}

const RetainerAgreementOverviewModal = ({
  isOpen,
  onClose,
  retainer: retainerProp,
  getExpertById,
  isLoading = false,
}: Props) => {
  const retainer = useLastVisible(retainerProp, isOpen);

  const expertIdForFeeSchedule = retainer?.matterExpert.expertId;
  const feeSchedule: FeeScheduleData = useMemo(() => {
    const expertForSchedule = expertIdForFeeSchedule
      ? getExpertById(expertIdForFeeSchedule)
      : undefined;
    if (expertForSchedule?.role === UserRole.EXPERT) {
      return feeScheduleFromMeExpertRates({
        hourlyRate: expertForSchedule.hourlyRate,
        recordsReviewHourlyRate: expertForSchedule.recordsReviewHourlyRate,
        examinationHourlyRate: expertForSchedule.examinationHourlyRate,
        depositionHourlyRate: expertForSchedule.depositionHourlyRate,
        testimonyHourlyRate: expertForSchedule.testimonyHourlyRate,
      });
    }
    return defaultFeeSchedule(expertForSchedule?.hourlyRate ?? 0);
  }, [expertIdForFeeSchedule, getExpertById]);

  if (!retainer) {
    return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        PaperComponent={StyledDialogPaper}
        maxWidth="sm"
        fullWidth
      >
        <ModalHeader title="Retainer overview" onClose={onClose} />
        <StyledDialogContent>
          {isLoading ? (
            <RetainerLoadingContainer
              role="status"
              aria-live="polite"
              aria-label="Loading retainer"
            >
              <CircularProgress size={32} aria-hidden />
            </RetainerLoadingContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Request not found.
            </Typography>
          )}
        </StyledDialogContent>
        <StyledDialogActions>
          <Button onClick={onClose}>Close</Button>
        </StyledDialogActions>
      </Dialog>
    );
  }

  const expert = getExpertById(retainer.matterExpert.expertId);
  const hoursParsed = (() => {
    const raw = retainer.hoursRequested;
    if (raw === null || raw === undefined || raw === '') {
      return 0;
    }
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  })();
  const isFlatRate =
    retainer.feeType === RetainerFeeType.FlatRate || hoursParsed === 0;
  const hourlyFeeDollars =
    retainer.hourlyFee !== null && retainer.hourlyFee !== undefined
      ? retainer.hourlyFee / CENTS_PER_DOLLAR
      : 0;
  const hourlyRateForLabel = isFlatRate ? 0 : hourlyFeeDollars;
  const hourlyRateDisplayContext = !isFlatRate
    ? getRetainerHourlyRateDisplayContext(retainer, feeSchedule)
    : null;
  const rateDisplayLabel =
    hourlyRateDisplayContext === null
      ? ''
      : getRateDisplayLabel(
          hourlyRateDisplayContext.rateKey,
          hourlyRateDisplayContext.customServiceLabel,
          hourlyRateForLabel,
        );
  const requestedDate = new Date(retainer.createdAt);
  const subtotalDollars = retainer.subtotal / CENTS_PER_DOLLAR;
  const platformFeeDollars = retainer.platformFee / CENTS_PER_DOLLAR;
  const totalDollars = retainer.total / CENTS_PER_DOLLAR;
  const descriptionText = retainerCardDescription(retainer);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperComponent={StyledDialogPaper}
      maxWidth="sm"
      fullWidth
    >
      <ModalHeader
        title="Retainer overview"
        subtitle="Summary and agreement status for this retainer."
        onClose={onClose}
      />
      <StyledDialogContent>
        <Stack spacing={2}>
          <RetainerExpertCard>
            <RetainerExpertCardAvatar
              avatarUrl={expert?.avatarUrl}
              initialsLabel={expert?.name ?? 'Expert'}
              alt=""
              aria-hidden
            />
            <RetainerExpertCardContent>
              {retainer.title && (
                <Typography variant="subtitle1" fontWeight={600}>
                  {retainer.title}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {expert?.name ?? 'Expert'}
                {isFlatRate ? ' · Flat fee' : ''}
              </Typography>
              {isValid(requestedDate) ? (
                <Typography variant="caption" color="text.secondary">
                  Requested {format(requestedDate, 'MMM d, yyyy')}
                </Typography>
              ) : null}
            </RetainerExpertCardContent>
          </RetainerExpertCard>
          <Typography variant="body2">{descriptionText}</Typography>
          <RetainerReceiptSummary
            isFlatRate={isFlatRate}
            flatAmount={isFlatRate ? subtotalDollars : 0}
            rateDisplayLabel={rateDisplayLabel}
            hours={isFlatRate ? 0 : hoursParsed}
            subtotal={subtotalDollars}
            platformFee={platformFeeDollars}
            total={totalDollars}
          />
          <AgreementSummaryBox>
            <AgreementSummaryTitle variant="subtitle2">
              Agreed to terms
            </AgreementSummaryTitle>
            <AgreementRow>
              <AgreementLabel>Attorney</AgreementLabel>
              {retainer.attorneyAgreedAt ? (
                <AgreementValue>
                  <CheckCircleOutline
                    fontSize="small"
                    color="success"
                    aria-hidden
                  />
                  Agreed on {formatAgreedAt(retainer.attorneyAgreedAt)}
                </AgreementValue>
              ) : (
                <AgreementValue>—</AgreementValue>
              )}
            </AgreementRow>
            <AgreementRow>
              <AgreementLabel>Expert</AgreementLabel>
              {retainer.expertAgreedAt ? (
                <AgreementValue>
                  <CheckCircleOutline
                    fontSize="small"
                    color="success"
                    aria-hidden
                  />
                  Agreed on {formatAgreedAt(retainer.expertAgreedAt)}
                </AgreementValue>
              ) : (
                <AgreementValue>—</AgreementValue>
              )}
            </AgreementRow>
          </AgreementSummaryBox>
        </Stack>
      </StyledDialogContent>
      <StyledDialogActions>
        <Button onClick={onClose}>Close</Button>
      </StyledDialogActions>
    </Dialog>
  );
};

const RetainerLoadingContainer = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 120,
  padding: theme.spacing(2),
}));

const AgreementSummaryBox = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  gap: theme.spacing(1.5),
}));

const AgreementSummaryTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
}));

const AgreementRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

const AgreementLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.secondary,
}));

const AgreementValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export default RetainerAgreementOverviewModal;
